import { AmplitudeFeatureFlagNames } from '@/libs/feature-flags';
import { CONCIERGE_WORKFLOW_IDS } from '@/providers/concierge/types';
import { routes } from '@/utils/routes';
import { WorkflowWorkflowState } from '@calm/evexia-web-sdk';
import { useAmplitudeExperiment } from '@rhg/hooks/use-experiments';
import { useAllWorkflowInstances } from '@rhg/hooks/use-workflows';
import { getIsLinkedSsoUser, getIsPreviouslySamlUser } from '@rhg/utils/sso';
import {
    FtueStoredStepName,
    FtueStepDefinitionIds,
    ConciergeFtueStepDefinitionIds,
    WorkflowIds,
} from '@rhg/utils/workflows';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useNeedsEmailOptInResponse } from '../registration/use-needs-email-opt-in-response';
import { useNeedsRegistrationConsent } from '../registration/use-needs-registration-consent';
import { useAccessCode } from '../use-access-code';
import useAccount from '../use-account';
import { useActiveCircle } from '../use-active-circle';
import { useAgeGenderStepStatus } from '../use-age-gender-step-status';
import useAppConfig from '../use-app-config';
import { useAutoSelectCircleState } from '../use-auto-select-circle-state';
import useCircles from '../use-circles';
import { useConsentContext } from '../use-consent-context';
import { useConsents } from '../use-consents';
import useHasAcceptedCohortTos from '../use-has-accepted-tos';
import { useInviteCode } from '../use-invite-code';
import { useIsConciergeOnboardingEnabled } from '../use-is-concierge-onboarding-enabled';
import { useIsSamlUser } from '../use-is-saml-user';
import { useMostRecentSponsorship } from '../use-most-recent-sponsorship';
import { useActivationTokenForPartner } from '../use-partner-activation';
import { usePartnerFromAccessCode } from '../use-partner-from-access-code';
import { useSponsorCode } from '../use-sponsor-code';
import { useStandaloneScreeningResults } from '../use-standalone-screening-results';
import { useTransitionPartner } from '../use-transition-partner';
import { UserBlocker } from './types';

/**
 * This hook determines if the user requires any blocking logic such as a redirect to a registration step, consent acceptance, reconsent.
 * The resulting logic is managed within the `userBlockerGate`.
 */
export function useUserBlocker() {
    const {
        NEXT_PUBLIC_AGE_GENDER_ENABLED_ISO_DATETIME: ageGenderEnabledDate,
    } = useAppConfig();
    const { isEnrolled: isFtueWorkflowEnabled } = useAmplitudeExperiment({
        experimentName: AmplitudeFeatureFlagNames.FTUE_WORKFLOW_ENABLED,
    });
    const { pathname } = useRouter();
    const { account, isLoading: isAccountLoading } = useAccount();
    const { activeCircle, isLoading: isActiveCircleLoading } =
        useActiveCircle();
    const {
        isConciergeOnboardingEnabled,
        isFetched: isConciergeOnboardingFetched,
    } = useIsConciergeOnboardingEnabled();
    const { hasInviteCode, isReady: isInviteCodeReady } = useInviteCode();
    const {
        hasAccessCode,
        isAccessCodeFromQuery,
        isReady: isAccessCodeReady,
    } = useAccessCode();
    const { brandingPartnerConfig, isFetched: isAccessCodePartnerFetched } =
        usePartnerFromAccessCode();
    const { destinationPartner, isLoading: isTransitionPartnerLoading } =
        useTransitionPartner(brandingPartnerConfig);
    const { hasSponsorCode } = useSponsorCode();
    const { hasActivationToken } = useActivationTokenForPartner(
        brandingPartnerConfig?.id
    );
    const { hasCircles, isLoading: isCirclesLoading } = useCircles();
    const { isCircleSelectionLoaded } = useAutoSelectCircleState();

    const { latestResult } = useStandaloneScreeningResults();
    const { consent, isLoading: isConsentLoading } =
        useHasAcceptedCohortTos(undefined);
    const {
        needsEmailOptInResponse,
        isLoading: isNeedsEmailOptInResponseLoading,
    } = useNeedsEmailOptInResponse();
    const { isLoading: isSponsorLoading, hasLostSponsorship } =
        useMostRecentSponsorship();
    const { consents, isLoading: isGetConsentsLoading } = useConsents();
    const isSamlUser = useIsSamlUser();
    const isPreviouslySamlUser = getIsPreviouslySamlUser();
    const isLinkedSsoUser = getIsLinkedSsoUser();
    const {
        data: needsRegistrationConsentData,
        isLoading: isNeedsRegistrationLoading,
    } = useNeedsRegistrationConsent();
    const accountConsents = consent?.consentData ? [consent.consentData] : [];
    const accountUserConsents = consent?.userResponse
        ? [consent.userResponse]
        : [];
    const { setHasCompletedAgeGenderStep } = useAgeGenderStepStatus();
    const { workflows = [], isFetched: isFetchedWorkflows } =
        useAllWorkflowInstances({
            workflowStates: [WorkflowWorkflowState.InProgress],
        });
    const unfinishedFtueWorkflows = workflows.filter(
        (workflow) =>
            workflow?.workflow.defnId === WorkflowIds.FTUE ||
            workflow?.workflow.defnId === WorkflowIds.FTUE_NO_VIDEO
    );
    const unfinishedConciergeFtueWorkflows = workflows.filter((workflow) =>
        CONCIERGE_WORKFLOW_IDS.includes(workflow?.workflow.defnId)
    );
    const storedStep = sessionStorage.getItem(FtueStoredStepName);

    const hasUnfinishedFtueWorkflow = useMemo(
        () =>
            isFtueWorkflowEnabled &&
            isFetchedWorkflows &&
            // Until the concierge permission settles it reads as disabled, which
            // would send a concierge user to the FTUE blocker instead of theirs.
            isConciergeOnboardingFetched &&
            !!unfinishedFtueWorkflows.length &&
            pathname !== routes.onboarding({}),
        [
            isFetchedWorkflows,
            isFtueWorkflowEnabled,
            isConciergeOnboardingFetched,
            unfinishedFtueWorkflows,
            pathname,
        ]
    );

    const hasUnfinishedConciergeFtueWorkflow = useMemo(
        () =>
            isConciergeOnboardingEnabled &&
            isFetchedWorkflows &&
            !!unfinishedConciergeFtueWorkflows.length &&
            pathname !== routes.concierge({}),
        [
            isFetchedWorkflows,
            isConciergeOnboardingEnabled,
            unfinishedConciergeFtueWorkflows,
            pathname,
        ]
    );

    // "Legacy" user in this context refers to users whose accounts were created before the ageGender feature was enabled.
    const isAgeGenderLegacyUser = useMemo(() => {
        if (account?.createdAt) {
            return new Date(account.createdAt) < ageGenderEnabledDate;
        }
        return false;
    }, [account?.createdAt, ageGenderEnabledDate]);

    const { consentRequirements } = useConsentContext({
        accountConsents,
        accountUserConsents,
        sponsorConsents: consents,
    });

    const parentalConsent = useMemo(
        () => consents?.find((c) => c.type === 'parental'),
        [consents]
    );

    const isLoading =
        isAccountLoading ||
        !isCircleSelectionLoaded ||
        !isInviteCodeReady ||
        !isAccessCodeReady ||
        isCirclesLoading ||
        isActiveCircleLoading ||
        isNeedsEmailOptInResponseLoading ||
        isSponsorLoading ||
        isConsentLoading ||
        isGetConsentsLoading ||
        isNeedsRegistrationLoading ||
        isTransitionPartnerLoading ||
        (hasAccessCode && isAccessCodeFromQuery && !isAccessCodePartnerFetched);

    if (isLoading) {
        return { isLoading, userBlocker: undefined };
    }

    if (!isSamlUser && (!hasCircles || !activeCircle)) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.Tos,
        };
    }

    if (hasLostSponsorship) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.Expired,
        };
    }

    if ((isSamlUser || isPreviouslySamlUser) && !isLinkedSsoUser) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.LinkingSSO,
        };
    }

    // if the user is using the promo code and has gone through the standalone screening then they need to be registered and post their results
    if (latestResult) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.PostMentalHealthScreeningPromo,
        };
    }

    // Should be before unsponsored CCHD since unsponsored CCHD will be covered in this flow if needed
    if (!activeCircle?.sponsor && !account?.countryCode) {
        return {
            isLoading: false,
            userBlocker:
                hasAccessCode &&
                isAccessCodeFromQuery &&
                (hasSponsorCode || hasActivationToken) &&
                !destinationPartner
                    ? UserBlocker.AutoSubmitMagicLink
                    : hasAccessCode && isAccessCodeFromQuery
                    ? UserBlocker.CountryUnsponsoredHasAccessCode
                    : hasInviteCode
                    ? // If invitation flow we handle the country in the invitation flow
                      UserBlocker.ReceivingInvitation
                    : UserBlocker.CountryUnsponsored,
        };
    }

    // Should be before sponsor in case unsponsored CCHD version is updated
    if (!activeCircle?.sponsor && consentRequirements.chdpp.isRequired) {
        if (
            !accountUserConsents[0].chdppVersion ||
            accountUserConsents[0].chdppVersion < 1
        ) {
            // If SSO flow, return to attempt joining again
            if (isLinkedSsoUser) {
                return {
                    isLoading: false,
                    userBlocker: UserBlocker.LinkingSSO,
                };
            }

            if (
                (hasSponsorCode || hasActivationToken) &&
                hasAccessCode &&
                isAccessCodeFromQuery &&
                !destinationPartner
            ) {
                return {
                    isLoading: false,
                    userBlocker: UserBlocker.AutoSubmitMagicLink,
                };
            }

            // If invitation flow we handle the CCHD in the invitation flow
            if (hasInviteCode) {
                return {
                    isLoading: false,
                    userBlocker: UserBlocker.ReceivingInvitation,
                };
            }

            // If non SSO flow, just prompt to accept consent
            return {
                isLoading: false,
                userBlocker: UserBlocker.CCHDUnsponsored,
            };
        } else {
            return {
                isLoading: false,
                userBlocker: UserBlocker.Reconsent,
            };
        }
    }

    // Should be after sponsored CCHD since we need to submit first to determine if the user still needs a country code
    if (activeCircle?.sponsor && !account?.countryCode) {
        return {
            isLoading: false,
            userBlocker: hasInviteCode
                ? // If invitation flow we handle the country in the invitation flow
                  UserBlocker.ReceivingInvitation
                : UserBlocker.CountrySponsored,
        };
    }

    const getAgeAndGenderBlocker = () => {
        if (!isAgeGenderLegacyUser && !account?.dob) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.RegistrationAgeGender,
            };
        }

        // This has to be before the unsponsored check otherwise the user can use the app without accepting by bailing out of the flow
        if (parentalConsent && consentRequirements.parental.isRequired) {
            if (
                !accountUserConsents[0].parentalVersion ||
                accountUserConsents[0].parentalVersion < 1
            ) {
                if (
                    needsRegistrationConsentData?.needsRegistrationConsent ===
                    'true'
                ) {
                    // Just in case the user no longer has the age/gender step cookie, we'll set it here to prevent an unnecessary step display
                    setHasCompletedAgeGenderStep();
                    return {
                        isLoading: false,
                        userBlocker: UserBlocker.RegistrationParentalConsent,
                    };
                }

                return {
                    isLoading: false,
                    userBlocker: UserBlocker.StandaloneParentalConsent,
                };
            } else {
                return {
                    isLoading: false,
                    userBlocker: UserBlocker.Reconsent,
                };
            }
        }
    };

    // If Unsponsored we want to return the blocker before the unsponsored check
    if (!activeCircle?.sponsor) {
        const ageAndGenderBlocker = getAgeAndGenderBlocker();
        if (ageAndGenderBlocker) {
            return ageAndGenderBlocker;
        }
    }

    // if the user still needs to consent to TOS/Privacy, CHDPP, or Partner Authorization, send them to re-consent flow
    if (
        consentRequirements.tos.isReconsent ||
        consentRequirements.privacy.isReconsent ||
        consentRequirements.chdpp.isReconsent ||
        consentRequirements.uk_international.isReconsent ||
        consentRequirements.in_international.isReconsent ||
        consentRequirements.cn_international.isReconsent ||
        consentRequirements.eu_international.isReconsent ||
        consentRequirements.au_international.isReconsent ||
        consentRequirements.international.isReconsent ||
        consentRequirements.parental.isReconsent ||
        consentRequirements.gina.isReconsent
    ) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.Reconsent,
        };
    }

    if (!activeCircle?.sponsor) {
        // If SSO flow, return to attempt joining again
        if (isLinkedSsoUser) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.LinkingSSO,
            };
        }

        /* If the user has not finished the onboarding workflow, send them to complete it */
        if (isConciergeOnboardingEnabled) {
            // When onboarding is concierge-driven, only redirect to concierge onboarding if there's an unfinished concierge workflow
            // If user has old FTUE workflow but is now concierge-driven (e.g., after sponsorship change),
            // don't block - let the success-step handle abandoning old workflow and starting new one
            if (
                hasUnfinishedConciergeFtueWorkflow &&
                storedStep !== ConciergeFtueStepDefinitionIds.END_WORKFLOW
            ) {
                return {
                    isLoading: false,
                    userBlocker: UserBlocker.ConciergeOnboarding,
                };
            }
        } else if (
            hasUnfinishedFtueWorkflow &&
            storedStep !== FtueStepDefinitionIds.END_WORKFLOW
        ) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.Onboarding,
            };
        }

        /**
         * No invitation handling here. If the user has an invitation, they will get a dialog from the registration dialog manager
         */
        return {
            isLoading: false,
            userBlocker: UserBlocker.Unsponsored,
        };
    }

    if (activeCircle?.sponsor && consentRequirements.chdpp.isRequired) {
        return { isLoading: false, userBlocker: UserBlocker.CCHDSponsored };
    }

    if (activeCircle?.sponsor) {
        if (consentRequirements.uk_international.isRequired) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.UK,
            };
        }

        if (consentRequirements.in_international.isRequired) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.India,
            };
        }

        if (consentRequirements.cn_international.isRequired) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.China,
            };
        }

        if (consentRequirements.eu_international.isRequired) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.EU,
            };
        }

        if (consentRequirements.au_international.isRequired) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.Australia,
            };
        }

        if (consentRequirements.international.isRequired) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.International,
            };
        }
    }

    // If Sponsored we want to return the blocker after the unsponsored check and country specific consents
    if (activeCircle?.sponsor) {
        const ageAndGenderBlocker = getAgeAndGenderBlocker();
        if (ageAndGenderBlocker) {
            return ageAndGenderBlocker;
        }
    }

    // Even if they are sponsored, we want to show this
    if (needsEmailOptInResponse) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.CommunicationsPreference,
        };
    }

    /* If the user has not finished the onboarding workflow, send them to complete it */
    if (isConciergeOnboardingEnabled) {
        // When onboarding is concierge-driven, only redirect to concierge onboarding if there's an unfinished concierge workflow
        // If user has old FTUE workflow but is now concierge-driven (e.g., after sponsorship change),
        // don't block - let the success-step handle abandoning old workflow and starting new one
        if (
            hasUnfinishedConciergeFtueWorkflow &&
            storedStep !== ConciergeFtueStepDefinitionIds.END_WORKFLOW
        ) {
            return {
                isLoading: false,
                userBlocker: UserBlocker.ConciergeOnboarding,
            };
        }
    } else if (
        hasUnfinishedFtueWorkflow &&
        storedStep !== FtueStepDefinitionIds.END_WORKFLOW
    ) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.Onboarding,
        };
    }

    // EVE-12675: a sponsored user whose sponsorship requires a consent they
    // have NEVER accepted (acceptedVersion === 0, e.g. a sponsorship assigned
    // via evexia-admin to an existing circle). Ordered last so every
    // registration / post-sponsorship blocker above takes precedence. The
    // previously-accepted (acceptedVersion > 0) path is the isReconsent gate
    // above.
    if (
        activeCircle?.sponsor &&
        ((consentRequirements.tos.isRequired &&
            !consentRequirements.tos.acceptedVersion) ||
            (consentRequirements.privacy.isRequired &&
                !consentRequirements.privacy.acceptedVersion) ||
            (consentRequirements.gina.isRequired &&
                !consentRequirements.gina.acceptedVersion))
    ) {
        return {
            isLoading: false,
            userBlocker: UserBlocker.Reconsent,
        };
    }

    return { isLoading: false, userBlocker: undefined };
}
